// @ts-nocheck
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMyOrganization } from "@/hooks/useMyOrganization";
import { Loader2, Upload, Pencil, Trash2, Power, X, Plus, BarChart3 } from "lucide-react";

type StatusFilter = "all" | "active" | "inactive";
type DateFilter = "all" | "running" | "upcoming" | "expired";

const CATEGORIES = ["Sponsor", "House", "Partner"];

interface AssetForm {
  id?: string;
  name: string;
  advertiser_name: string;
  image_url: string;
  click_url: string;
  alt_text: string;
  category: string;
  start_date: string;
  end_date: string;
  notes: string;
  is_active: boolean;
  width?: number;
  height?: number;
}

const emptyForm: AssetForm = {
  name: "",
  advertiser_name: "",
  image_url: "",
  click_url: "",
  alt_text: "",
  category: "Sponsor",
  start_date: "",
  end_date: "",
  notes: "",
  is_active: true,
};

function formatDateRange(start?: string | null, end?: string | null): string | null {
  if (!start && !end) return null;
  const fmt = (d: string) =>
    new Date(d + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  if (start && end) return `${fmt(start)} — ${fmt(end)}`;
  if (start) return `From ${fmt(start)}`;
  return `Until ${fmt(end!)}`;
}

function getDateStatus(asset: any): "running" | "upcoming" | "expired" | null {
  const today = new Date().toISOString().slice(0, 10);
  if (asset.end_date && asset.end_date < today) return "expired";
  if (asset.start_date && asset.start_date > today) return "upcoming";
  if (asset.start_date || asset.end_date) return "running";
  return null;
}

function AssetModal({
  initial,
  orgId,
  userId,
  onClose,
  onSaved,
}: {
  initial: AssetForm;
  orgId: string;
  userId?: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<AssetForm>(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (patch: Partial<AssetForm>) => setForm((f) => ({ ...f, ...patch }));

  const handleFile = async (file: File) => {
    setError(null);
    if (!file.type.startsWith("image/")) {
      setError("Please upload an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB.");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "png";
      const path = `${orgId}/${crypto.randomUUID()}.${ext}`;
      const { error: upErr } = await (supabase as any).storage
        .from("ad-assets")
        .upload(path, file, { cacheControl: "3600", upsert: false });
      if (upErr) throw upErr;
      const { data: pub } = (supabase as any).storage.from("ad-assets").getPublicUrl(path);
      const url = pub?.publicUrl;
      if (!url) throw new Error("Could not get public URL.");

      // Read dimensions
      const img = new Image();
      img.onload = () => update({ image_url: url, width: img.width, height: img.height });
      img.onerror = () => update({ image_url: url });
      img.src = url;
    } catch (e: any) {
      setError(e.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    if (!form.name.trim()) return setError("Name is required.");
    if (!form.image_url) return setError("Please upload an image.");
    setSaving(true);
    try {
      const payload: any = {
        organization_id: orgId,
        name: form.name.trim(),
        advertiser_name: form.advertiser_name.trim() || null,
        image_url: form.image_url,
        click_url: form.click_url.trim() || null,
        alt_text: form.alt_text.trim() || form.name.trim(),
        category: form.category || null,
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        notes: form.notes.trim() || null,
        is_active: form.is_active,
        width: form.width || null,
        height: form.height || null,
      };

      if (form.id) {
        const { error: upErr } = await (supabase as any)
          .from("ad_assets")
          .update(payload)
          .eq("id", form.id);
        if (upErr) throw upErr;
      } else {
        payload.created_by = userId || null;
        const { error: insErr } = await (supabase as any).from("ad_assets").insert(payload);
        if (insErr) throw insErr;
      }
      onSaved();
      onClose();
    } catch (e: any) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-surface-card border border-stroke-subtle rounded-token-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-stroke-subtle">
          <h2 className="text-heading-3 text-ink-primary">
            {form.id ? "Edit Ad Asset" : "Upload Ad Asset"}
          </h2>
          <button onClick={onClose} className="text-ink-tertiary hover:text-ink-primary">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Image */}
          <div>
            <label className="block text-body-sm font-medium text-ink-primary mb-2">
              Banner Image *
            </label>
            {form.image_url ? (
              <div className="relative aspect-[16/9] bg-surface-sunken rounded-token-md overflow-hidden border border-stroke-subtle">
                <img src={form.image_url} alt="" className="w-full h-full object-contain" />
                <label className="absolute bottom-2 right-2 bg-cyan text-cyan-foreground hover:bg-cyan-hover px-3 py-1.5 rounded-token-md text-body-sm font-medium cursor-pointer inline-flex items-center gap-1.5">
                  <Upload className="h-3.5 w-3.5" /> Replace
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFile(f);
                      e.currentTarget.value = "";
                    }}
                  />
                </label>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center aspect-[16/9] bg-surface-sunken border-2 border-dashed border-stroke-subtle rounded-token-md cursor-pointer hover:border-cyan transition-colors">
                {uploading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-ink-tertiary" />
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-ink-tertiary mb-2" />
                    <span className="text-body-sm text-ink-secondary">Click to upload</span>
                    <span className="text-caption text-ink-tertiary mt-1">PNG, JPG up to 5MB</span>
                  </>
                )}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleFile(f);
                    e.currentTarget.value = "";
                  }}
                />
              </label>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name *">
              <input
                value={form.name}
                onChange={(e) => update({ name: e.target.value })}
                className="input-base"
              />
            </Field>
            <Field label="Advertiser Name">
              <input
                value={form.advertiser_name}
                onChange={(e) => update({ advertiser_name: e.target.value })}
                className="input-base"
              />
            </Field>
            <Field label="Click URL">
              <input
                type="url"
                placeholder="https://..."
                value={form.click_url}
                onChange={(e) => update({ click_url: e.target.value })}
                className="input-base"
              />
            </Field>
            <Field label="Category">
              <select
                value={form.category}
                onChange={(e) => update({ category: e.target.value })}
                className="input-base"
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Alt Text">
              <input
                value={form.alt_text}
                onChange={(e) => update({ alt_text: e.target.value })}
                placeholder="(defaults to name)"
                className="input-base"
              />
            </Field>
            <Field label="">
              <label className="flex items-center gap-2 mt-7">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => update({ is_active: e.target.checked })}
                />
                <span className="text-body-sm text-ink-primary">Active</span>
              </label>
            </Field>
            <Field label="Start Date">
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => update({ start_date: e.target.value })}
                className="input-base"
              />
            </Field>
            <Field label="End Date">
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => update({ end_date: e.target.value })}
                className="input-base"
              />
            </Field>
          </div>

          <Field label="Notes">
            <textarea
              value={form.notes}
              onChange={(e) => update({ notes: e.target.value })}
              rows={3}
              className="input-base resize-y"
            />
          </Field>

          {error && (
            <div className="text-body-sm text-status-danger bg-status-danger/10 px-3 py-2 rounded-token-md">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-stroke-subtle">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-token-md text-body-sm border border-stroke-subtle hover:bg-surface-sunken"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || uploading}
            className="px-5 py-2 rounded-token-md text-body-sm font-medium bg-cyan text-cyan-foreground hover:bg-cyan-hover disabled:opacity-50 inline-flex items-center gap-2"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {form.id ? "Save Changes" : "Create Asset"}
          </button>
        </div>
      </div>

      <style>{`
        .input-base {
          width: 100%;
          padding: 0.5rem 0.75rem;
          border-radius: 0.5rem;
          border: 1px solid hsl(var(--stroke-subtle, 220 13% 91%));
          background: hsl(var(--surface-page, 0 0% 100%));
          color: hsl(var(--ink-primary, 222 47% 11%));
          font-size: 0.875rem;
        }
        .input-base:focus {
          outline: none;
          border-color: hsl(var(--cyan, 199 89% 48%));
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && (
        <label className="block text-body-sm font-medium text-ink-primary mb-1.5">{label}</label>
      )}
      {children}
    </div>
  );
}

export default function AdAssetsPage() {
  const { user } = useAuth();
  const { organization } = useMyOrganization();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AssetForm | null>(null);

  const { data: assets = [], isLoading, refetch } = useQuery({
    queryKey: ["ad-assets", search, statusFilter],
    queryFn: async () => {
      let q = (supabase as any)
        .from("ad_assets")
        .select("*")
        .order("created_at", { ascending: false });
      if (statusFilter === "active") q = q.eq("is_active", true);
      if (statusFilter === "inactive") q = q.eq("is_active", false);
      if (search.trim()) {
        const s = search.trim();
        q = q.or(`name.ilike.%${s}%,advertiser_name.ilike.%${s}%`);
      }
      const { data, error } = await q;
      if (error) throw error;
      return data || [];
    },
  });

  const filteredAssets = useMemo(() => {
    if (dateFilter === "all") return assets;
    return assets.filter((a: any) => {
      const status = getDateStatus(a);
      if (dateFilter === "running") return status === "running" || status === null;
      return status === dateFilter;
    });
  }, [assets, dateFilter]);

  const openCreate = () => {
    setEditing({ ...emptyForm });
    setModalOpen(true);
  };

  const openEdit = (a: any) => {
    setEditing({
      id: a.id,
      name: a.name || "",
      advertiser_name: a.advertiser_name || "",
      image_url: a.image_url || "",
      click_url: a.click_url || "",
      alt_text: a.alt_text || "",
      category: a.category || "Sponsor",
      start_date: a.start_date || "",
      end_date: a.end_date || "",
      notes: a.notes || "",
      is_active: a.is_active !== false,
      width: a.width,
      height: a.height,
    });
    setModalOpen(true);
  };

  const toggleActive = async (a: any) => {
    await (supabase as any)
      .from("ad_assets")
      .update({ is_active: !a.is_active })
      .eq("id", a.id);
    refetch();
  };

  const handleDelete = async (a: any) => {
    if (!confirm(`Delete "${a.name}"? This will also remove any placements.`)) return;
    await (supabase as any).from("ad_assets").delete().eq("id", a.id);
    refetch();
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-display-sm text-ink-primary font-display">Ad Assets</h1>
        <p className="text-body-md text-ink-secondary mt-2 max-w-2xl">
          Banner library for sponsor placements on event pages and in emails.
          Upload an asset once, then attach it to specific events via the event wizard.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 bg-cyan text-cyan-foreground hover:bg-cyan-hover px-4 py-2 rounded-token-md font-medium transition-colors"
          >
            <Plus className="h-4 w-4" /> New Asset
          </button>
          <a
            href="/admin-dashboard/advertising/performance"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-token-md border border-stroke-subtle hover:bg-surface-sunken text-body-sm font-medium"
          >
            <BarChart3 className="h-4 w-4" /> Performance
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <input
          type="search"
          placeholder="Search by name or advertiser…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[220px] px-3 py-2 rounded-token-md border border-stroke-subtle bg-surface-page text-body-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          className="px-3 py-2 rounded-token-md border border-stroke-subtle bg-surface-page text-body-sm"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value as DateFilter)}
          className="px-3 py-2 rounded-token-md border border-stroke-subtle bg-surface-page text-body-sm"
        >
          <option value="all">All dates</option>
          <option value="running">Currently running</option>
          <option value="upcoming">Upcoming</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-ink-tertiary" />
        </div>
      ) : filteredAssets.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-stroke-subtle rounded-token-xl">
          <p className="text-body-md text-ink-tertiary">No ad assets yet.</p>
          <button
            onClick={openCreate}
            className="mt-4 text-body-sm text-cyan hover:underline"
          >
            Upload your first asset
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filteredAssets.map((a: any) => {
            const dateStatus = getDateStatus(a);
            const range = formatDateRange(a.start_date, a.end_date);
            return (
              <div
                key={a.id}
                className="bg-surface-card border border-stroke-subtle rounded-token-xl p-3 flex items-center gap-4"
              >
                <div className="h-16 w-28 flex-shrink-0 rounded-token-md bg-surface-sunken overflow-hidden">
                  {a.image_url && (
                    <img
                      src={a.image_url}
                      alt={a.alt_text || a.name}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-heading-4 text-ink-primary truncate">{a.name}</h3>
                    {a.is_active ? (
                      <span className="text-caption px-2 py-0.5 rounded-full bg-cyan/15 text-cyan">
                        Active
                      </span>
                    ) : (
                      <span className="text-caption px-2 py-0.5 rounded-full bg-surface-sunken text-ink-tertiary">
                        Inactive
                      </span>
                    )}
                    {dateStatus === "expired" && (
                      <span className="text-caption px-2 py-0.5 rounded-full bg-status-warning/15 text-status-warning">
                        Expired
                      </span>
                    )}
                    {dateStatus === "upcoming" && (
                      <span className="text-caption px-2 py-0.5 rounded-full bg-status-info/15 text-status-info">
                        Upcoming
                      </span>
                    )}
                    {a.category && (
                      <span className="text-caption px-2 py-0.5 rounded-full bg-surface-sunken text-ink-secondary">
                        {a.category}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-caption text-ink-tertiary">
                    {a.advertiser_name && <span className="truncate">{a.advertiser_name}</span>}
                    {a.advertiser_name && range && <span>·</span>}
                    {range && <span>{range}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    onClick={() => openEdit(a)}
                    title="Edit"
                    className="p-2 rounded-token-md border border-stroke-subtle hover:bg-surface-sunken"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => toggleActive(a)}
                    title={a.is_active ? "Deactivate" : "Activate"}
                    className="p-2 rounded-token-md border border-stroke-subtle hover:bg-surface-sunken"
                  >
                    <Power className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(a)}
                    title="Delete"
                    className="p-2 rounded-token-md border border-stroke-subtle hover:border-status-danger hover:text-status-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalOpen && editing && (
        <AssetModal
          initial={editing}
          orgId={organization?.id}
          userId={user?.id}
          onClose={() => setModalOpen(false)}
          onSaved={refetch}
        />
      )}
    </div>
  );
}
